import * as THREE from "three";
import { BalanceData, Cell, Grid, Heading } from "@highmarch/domain";
import { SceneRig } from "./scene";
import { GridView, SurroundView, hqTowerName, lib, surfaceY } from "./views";
import { SkyClouds } from "./skyClouds";
import { GroundMesh } from "./groundMesh";
import { GroundContacts } from "./groundSlope";
import { Terrain } from "./terrain";
import { HqMaterials } from "./hqMaterials";
import { HUB_BUDGET, countTriangles, modelsThatFit } from "./hubBudget";
import { HubInsets, HubLayout, HubTarget, isPetTarget } from "./hubLayout";
import { RenderScheduler } from "./hubRender";
import { HubStand, placeHubModel } from "./hubLod";
import { HubScenery } from "./hubScenery";
import { HubCamp } from "./hubCamp";
import { HubRaid } from "./hubRaid";
import { HubFx, HubModel } from "./hubFx";
import { HubGhost } from "./hubGhost";
import { HubCritters } from "./views/critterHub";
import { HubPets } from "./views/petHubPack";
import { RigidRig } from "./rigidRig";
import { FortressCompanion } from "./views/fortressCompanion";
import { HubSceneZoom } from "./hubSceneZoom";
import { HubShadows } from "./shadowCadence";

export type { HubInsets, HubTarget } from "./hubLayout";

/**
 * The hub's hero shot (P1.10.3, reworked for the lobby): the fortress with the owned towers on stands in a crescent
 * round it, at their persistent tiers, in the current biome, under the painted sky. A still camera — nothing drifts
 * (the owner: "the entire base rotating around doesn't make sense as trees are moving"); the only motion is the
 * models' own idle clips, the chimney smoke and the flags, plus what tells the player something (hubFx.ts: the
 * scaffold on an upgrade, a celebration when one finishes; hubGhost.ts: the next tower to unlock). The player's
 * pinch zooms the shot in for a closer look and back (hubZoom.ts, input/hubGestures.ts); it never turns.
 *
 * Rendering is on demand (hubRender.ts): ambient loops at 60 fps, full rate only while something one-off plays,
 * and nothing at all while paused under a full-screen sheet. Pure presentation: it reads the profile once.
 */
export class HubScene {
  // 17 × 17 since the war camp (2026-10-22): the camp's palisade, tents and road run past the old 9 × 11 lawn, and
  // the ground (and its haze) has to reach them
  static readonly Width = 17;
  static readonly Height = 17;
  /** The fortress's cell; the stands are whole-cell offsets from it (HubLayout.Stands). */
  static readonly BaseCell = new Cell(6, 6);
  /** Seconds of full-rate frames after a re-frame, so the chips following `onLayout` never trail a stale shot. */
  static readonly ReframeSeconds = 0.25;

  private readonly group = new THREE.Group();
  private readonly grid: Grid;
  private readonly ground: GroundMesh;
  private readonly gridView: GridView | null = null;
  private readonly scenery: HubScenery | null = null;
  /** The frontier war camp round the line-up (hubCamp.ts); none on the tile-kit fallback. */
  private readonly camp: HubCamp | null = null;
  /** The raids the fortress strikes down at the camp gate (hubRaid.ts); only with the camp. */
  private raid: HubRaid | null = null;
  /** Rabbits, a bird and butterflies on the lawn (views/critterHub.ts); none on the tile-kit fallback. */
  private readonly critters: HubCritters | null = null;
  /** Every pet had, on the lawn near the fortress (views/petHubPack.ts, docs/PETS.md); none on the tile-kit fallback. */
  private readonly pet: HubPets | null = null;
  private readonly petBox = new THREE.Box3();
  private readonly sky: SkyClouds;
  private readonly fx: HubFx;
  private readonly fortress: HubModel;
  /** The fortress model's name (`base_t2__dragonlord`) and its skin's flying companions, if it keeps any. */
  private fortressName = "";
  private readonly companion: FortressCompanion | null = null;
  private readonly stands: HubStand[] = [];
  private readonly reduced: THREE.BufferGeometry[] = [];
  private ghost: HubGhost | null = null;
  private ghostWanted: string | null = null;
  private insets: HubInsets | null = null;
  private readonly scheduler = new RenderScheduler();
  /** The shot's framing, the pinch zoom over it and the chips following it (hubSceneZoom.ts). */
  private readonly view: HubSceneZoom;
  private raf = 0;
  private size = { w: 0, h: 0 };
  private disposed = false;
  /** Triangles the hub draws once built (for the perf overlay / captures). */
  triangles = 0;

  constructor(private readonly rig: SceneRig, balance: BalanceData, owned: readonly boolean[], towerLevels: readonly number[]) {
    const baseCell = HubScene.BaseCell;
    this.grid = new Grid(HubScene.Width, HubScene.Height, baseCell, Heading.North, []);
    Terrain.current = new Terrain(this.grid, 9187, 0);         // the hub is flat; the mesh still welds it
    // The props have to be told where the map edge is, or they do not fade with the ground and the hub's trees hang
    // crisp in the haze the ground has already dissolved into.
    HqMaterials.setMapHaze(-0.5, -0.5, HubScene.Width, HubScene.Height, rig.lookNow.sky, GroundMesh.FogStart, GroundMesh.FogFull);
    GroundContacts.clear();                                     // a new scene: the last one's contact shadows go
    this.ground = new GroundMesh(this.grid, Terrain.current);
    // the camp's road out to battle and its trodden yards, painted into the ground's splat
    this.ground.worn = HubCamp.worn(baseCell);
    const roadCells = HubCamp.roadCells(baseCell).filter((c) => this.grid.inBounds(c));
    this.ground.build({ roadCells } as unknown as Parameters<GroundMesh["build"]>[0]);
    this.group.add(new SurroundView(this.grid).group, this.ground.group);
    if (GroundMesh.available) {
      this.scenery = new HubScenery(baseCell, surfaceY());
      this.group.add(this.scenery.group);
      HubScenery.seatFortress(this.grid.footprint);
    } else {
      this.gridView = new GridView(this.grid);                  // the tile kit is the ground when the maps did not ship
      this.group.add(this.gridView.group);
    }
    // Triangle budget (hubBudget.ts): the fortress and as many stands as fit in its model share, all at hub detail.
    // No micro-detail scatter: its tufts cost ~60 draw calls here for grass the hub's shot shows as texture anyway.
    const base = balance.towers.findIndex((t) => t.isBase);
    const fortress = placeHubModel(balance.towers[base].id, true, HubScene.tierFor(towerLevels[base]), baseCell, this.reduced);
    this.fortress = fortress; this.fortressName = fortress.name;
    const towers = balance.towers.filter((t) => !t.isBase && owned[t.index]).sort((a, b) => a.unlockLevel - b.unlockLevel || a.index - b.index);
    const stands: HubStand[] = [];
    for (const t of towers) {
      const cell = HubLayout.standCell(baseCell, stands.length);
      if (!cell) break;
      stands.push({ ...placeHubModel(t.id, false, HubScene.tierFor(towerLevels[t.index]), cell, this.reduced), id: t.id, cell });
    }
    const keep = modelsThatFit([this.fortress, ...stands].map((m) => countTriangles(m.root)), HUB_BUDGET.Budget * HUB_BUDGET.ModelShare);
    this.group.add(this.fortress.root);
    // a premium fortress's flying companions (the Dragonlord's drake, the moon owls) circle it in the hub too
    this.companion = FortressCompanion.of(this.fortress.root, this.fortressName, this.fortress.box, this.group);
    for (const s of stands.slice(0, Math.max(0, keep - 1))) { this.group.add(s.root); this.stands.push(s); }
    if (GroundMesh.available) {
      // every stand cell, not just the filled ones: a critter must not sit where the next tower's ghost appears
      const standCells = HubLayout.Stands.map((_, i) => HubLayout.standCell(baseCell, i)!);
      this.critters = new HubCritters(this.grid, baseCell, standCells);
      this.group.add(this.critters.group);
      this.pet = new HubPets(this.grid, baseCell, standCells);
      this.group.add(this.pet.group);
      // `?nocamp` leaves the camp out (perf side-by-sides)
      if (typeof location === "undefined" || !new URLSearchParams(location.search).has("nocamp")) {
        this.camp = new HubCamp(baseCell, surfaceY(), standCells, this.grid.footprint);
        this.group.add(this.camp.group);
      }
    }
    this.paintPads();
    rig.scene.add(this.group);
    this.sky = new SkyClouds(rig.scene, rig.camera);
    this.fx = new HubFx(rig.scene, rig.camera);
    if (this.pet) this.pet.fx = () => this.fx.ambient();
    if (this.camp) { this.raid = new HubRaid(balance, baseCell, surfaceY(), this.fortress.root, this.fortressName, () => this.fx.ambient(), () => rig.quality.index); this.group.add(this.raid.group); }
    rig.frame(this.grid);
    // the shadow map follows the moving casters on every rendered frame (shadowCadence.ts: the per-tier rule, its cost)
    HubShadows.start(rig, this.stands.length + 1 + (this.camp?.animated ?? 0));
    this.view = new HubSceneZoom(rig);
    this.reframe();
    this.raf = requestAnimationFrame(this.tick);
  }

  /** Persistent level shown as the in-level tier look: 1 → tier 1, 10+ → tier 2, 30+ → tier 3. */
  static tierFor(level: number): number { return level >= 30 ? 3 : level >= 10 ? 2 : 1; }

  // ------------------------------------------------------------------------------------------------ the hub UI's API

  /** Re-frames the shot for the UI bands covering the canvas (CSS px); the shot sits in the band between them. */
  setFrame(insets: HubInsets): void {
    this.insets = { top: insets.top, bottom: insets.bottom };
    this.reframe();
  }

  /**
   * Where a DOM chip for `what` goes: CSS px (page coordinates, like getBoundingClientRect), centred over the model and
   * just above its top on screen — put the chip's bottom centre there. It follows the shot — call it again after `setFrame` or from `onLayout`. Null when the fortress or that
   * tower is not standing in the hub (a tower the player does not own, one left out by the budget).
   */
  anchorOnScreen(what: HubTarget): { x: number; y: number } | null {
    const model = this.modelFor(what);
    if (!model) return null;
    const r = this.rig.canvas.getBoundingClientRect();
    const v = HubLayout.anchorOver(this.rig.iso, model.box);
    return { x: r.left + v.x, y: r.top + v.y };
  }

  /** Calls `fn` whenever the shot is re-framed (setFrame, a resize, the ghost appearing); returns the unsubscribe. */
  onLayout(fn: () => void): () => void { return this.view.onLayout(fn); }

  /** Shows the next tower to unlock as a ghost on the next free stand (null removes it). */
  showNextTowerGhost(towerId: string | null): void {
    this.ghostWanted = towerId;
    if (this.ghost && this.ghost.towerId === towerId) return;
    this.ghost?.dispose();
    this.ghost = null;
    if (towerId === null) { this.paintPads(); this.reframe(); return; }
    const L = lib();
    const name = hqTowerName(towerId, false, 1, null);
    if (L && !L.hq(name) && L.ships(name)) { void L.ensure([name]).then(() => { if (!this.disposed && this.ghostWanted === towerId) this.showNextTowerGhost(towerId); }); return; }
    const cell = HubLayout.standCell(HubScene.BaseCell, this.stands.length);
    if (!cell) return;
    this.ghost = new HubGhost(towerId, new THREE.Vector3(cell.x, surfaceY(), cell.y));
    this.group.add(this.ghost.root);
    this.ghost.root.updateMatrixWorld(true);
    this.paintPads();
    this.reframe();
  }

  /** The fortress or a tower under upgrade wears its scaffold and puffs dust; null clears it. */
  setUpgrading(target: HubTarget | null): void {
    // a pet has no scaffold: the builders strip and the sheet carry its timer
    this.fx.setUpgrading(target && !isPetTarget(target) ? this.modelFor(target, false) : null);
    this.scheduler.invalidate();
  }

  /** One-shot: the target's `build` clip and a gold burst (an upgrade finished, a tower newly unlocked). */
  celebrate(target: HubTarget): void {
    const model = this.modelFor(target, false);
    if (!model) return;
    if (isPetTarget(target)) { this.pet?.cheer(target.pet); this.scheduler.burst(this.fx.sparkle(model), performance.now()); return; }
    this.scheduler.burst(this.fx.celebrate(model), performance.now());
  }

  /** Stops rendering entirely (a full-screen sheet covers the scene) or resumes it. */
  setPaused(on: boolean): void {
    this.scheduler.setPaused(on);
    if (on) { cancelAnimationFrame(this.raf); this.raf = 0; }
    else if (!this.raf && !this.disposed) this.raf = requestAnimationFrame(this.tick);
  }

  /** Whether a pinch or a drag on the scene is under way or just ended: a click on a chip now is part of it, not a tap. */
  get gestureBusy(): boolean { return this.view.busy; }

  /** Frames rendered since the hub was built (captures and the perf probe measure the idle rate with it). */
  get framesRendered(): number { return this.scheduler.frames; }

  // ------------------------------------------------------------------------------------------------------ internals

  private modelFor(what: HubTarget, ghost = true): HubModel | null {
    if (what === "fortress") return this.fortress;
    if (isPetTarget(what)) { const box = this.pet?.box(what.pet, this.petBox), root = this.pet?.groupOf(what.pet); return box && root ? { root, rig: this.fortress.rig, box } : null; }
    const s = this.stands.find((m) => m.id === what.tower);
    if (s) return s;
    return ghost && this.ghost?.towerId === what.tower ? { root: this.ghost.root, rig: this.fortress.rig, box: this.ghost.box } : null;
  }

  /** Each stand (the ghost's too) is a flagstone pad with a contact shadow, as a tower on a map stands. */
  private paintPads(): void {
    const cells = this.stands.map((s) => s.cell);
    if (this.ghost) { const c = HubLayout.standCell(HubScene.BaseCell, this.stands.length); if (c) cells.push(c); }
    this.ground.setTowers(cells.map((cell) => ({ alive: true, cell })));
  }

  /** Fits the fortress, the stands and the ghost into the band between the insets, and tells the chips. */
  private reframe(): void {
    const c = this.rig.canvas;
    this.size = { w: c.clientWidth || window.innerWidth, h: c.clientHeight || window.innerHeight };
    const insets = HubLayout.clampInsets(this.insets ?? HubLayout.defaultInsets(this.size.h), this.size.h);
    const boxes = [this.fortress.box, ...this.stands.map((s) => s.box), ...(this.ghost ? [this.ghost.box] : [])];
    // the camp's extent is framed too, so the home shot is the same war camp at level 1 and level 60
    const points = [...HubLayout.framePoints(boxes), ...(this.camp ? HubCamp.framePoints(HubScene.BaseCell, surfaceY()) : [])];
    // the pet keeps to the lawn the default shot shows (views/petHub.ts): in the band, clear of its edges
    const towers = [...this.stands.map((s) => s.cell), ...[HubLayout.standCell(HubScene.BaseCell, this.stands.length)].filter((c): c is Cell => c !== null)];   // and the next one's ghost
    this.view.frame(points, insets, this.size.w, this.size.h, surfaceY(), (inShot) => this.pet?.frame(inShot, towers));
    this.triangles = countTriangles(this.group);
    this.scheduler.burst(HubScene.ReframeSeconds, performance.now());
    this.view.notify();
  }

  private tick = (now: number): void => {
    this.raf = requestAnimationFrame(this.tick);
    const c = this.rig.canvas;
    if ((c.clientWidth || window.innerWidth) !== this.size.w || (c.clientHeight || window.innerHeight) !== this.size.h) this.reframe();
    const dt = this.scheduler.step(now);
    if (dt === null) return;
    this.fortress.rig.update(dt);
    if (this.companion) { this.companion.update(dt, null); this.scheduler.invalidate(); }
    for (const s of this.stands) { if (s.idle) s.idle.update(dt); else s.rig.update(dt); }
    this.critters?.update(dt);
    this.camp?.update(dt);
    this.raid?.update(dt);
    this.pet?.update(dt);
    if (this.fx.update(dt)) this.scheduler.invalidate();
    this.gridView?.update(dt);
    this.sky.update(dt);
    HubShadows.follow(this.rig);                               // the governor may change the tier under the hub
    this.rig.update(dt);
    this.view.follow();
    this.rig.render();
  };

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.view.dispose();
    this.fx.dispose();
    this.ghost?.dispose();
    this.sky.dispose();
    this.ground.dispose();
    this.scenery?.dispose();
    this.critters?.dispose();
    this.camp?.dispose();
    this.raid?.dispose();
    this.pet?.dispose();
    this.gridView?.dispose();
    for (const g of this.reduced) g.dispose();
    this.companion?.dispose();
    for (const m of [this.fortress, ...this.stands]) RigidRig.dispose(m.root);   // bone slots, the chimney smoke
    HubShadows.stop(this.rig);
    Terrain.current = null;
    this.rig.scene.remove(this.group);
  }
}
